import { create } from 'zustand';
import type { AnswerValue, FormAnswers } from '@/components/common/form/types';

/**
 * Drawer の表示状態
 * - auth: 認証方法選択画面（未ログインユーザー向け：ログイン/新規登録ボタン）
 * - form: URL 入力 + お問い合わせフォーム（ログイン済みユーザー向け）
 * - diagnostic: 診断フォーム（マルチステップ）
 * - completed: 送信完了画面
 * - null: Drawer を閉じた状態
 *
 * 認証フロー：
 * 未ログインユーザーがボタンを押すとラクスル認証サーバーにリダイレクト。
 * 認証完了後、コールバックでフロントエンドに戻り、formビューが開く。
 */
export type DrawerView =
  | 'auth'
  | 'form'
  | 'diagnostic'
  | 'completed'
  | null;

/**
 * URL 入力フォームのデータ
 */
export interface UrlFormData {
  websiteUrl: string;
  companyName: string;
  contactName: string;
  email: string;
  message: string;
}

/**
 * Drawer 状態管理
 */
interface DrawerState {
  // 現在の Drawer 表示状態
  activeView: DrawerView;

  // フォームデータ（Drawer 間で共有）
  formData: UrlFormData;

  // 診断フォーム回答データ（スキーマ駆動）
  diagnosticAnswers: FormAnswers;

  // Actions
  setActiveView: (view: DrawerView) => void;
  setFormData: (data: Partial<UrlFormData>) => void;
  setAnswer: (questionId: string, answer: AnswerValue) => void;
  resetFormData: () => void;
  resetDiagnosticData: () => void;
  openDrawer: (view: DrawerView) => void;
  closeDrawer: () => void;
}

/**
 * フォームデータの初期値
 */
const initialFormData: UrlFormData = {
  websiteUrl: '',
  companyName: '',
  contactName: '',
  email: '',
  message: '',
};

/**
 * Drawer 状態管理 Store
 * Half-modal の開閉と遷移を管理
 */
export const useDrawerStore = create<DrawerState>((set) => ({
  activeView: null,
  formData: { ...initialFormData },
  diagnosticAnswers: {},

  setActiveView: (view) => set({ activeView: view }),

  setFormData: (data) =>
    set((state) => ({
      formData: { ...state.formData, ...data },
    })),

  setAnswer: (questionId, answer) =>
    set((state) => ({
      diagnosticAnswers: { ...state.diagnosticAnswers, [questionId]: answer },
    })),

  resetFormData: () => set({ formData: { ...initialFormData } }),

  resetDiagnosticData: () => set({ diagnosticAnswers: {} }),

  openDrawer: (view) => set({ activeView: view }),

  closeDrawer: () =>
    set({
      activeView: null,
      formData: { ...initialFormData },
      diagnosticAnswers: {},
    }),
}));
